import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceDot,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  formatHandgripChartAgeBand,
  formatHandgripPercentile,
  getHandgripChartAxisConfig,
  getHandgripChartData,
  getHandgripDominantHandLabel,
  normalizeHandgripPayload,
  summarizeHandgripResult,
  type HandgripChartImages,
  type HandgripResultPayload,
} from '../handgrip/handgripDomain';
import { TOMKINSON_HANDGRIP_2025_PERCENTILES } from '../handgrip/handgripTomkinson2025';
import { resolveAssessmentInterpretationText } from '../utils/interpretationOverride';

export const getHandgripSeverityLabel = (percentile?: number | null): string => {
  if (typeof percentile !== 'number') return 'Sem referência normativa';
  if (percentile < 5) return 'Muito abaixo do esperado';
  if (percentile < 25) return 'Abaixo do esperado';
  if (percentile <= 75) return 'Faixa esperada';
  if (percentile <= 95) return 'Acima do esperado';
  return 'Muito acima do esperado';
};

export const getHandgripSeverityTone = (percentile?: number | null): string => {
  if (typeof percentile !== 'number') return 'bg-slate-100 text-slate-700';
  if (percentile < 5) return 'bg-rose-100 text-rose-700';
  if (percentile < 25) return 'bg-amber-100 text-amber-700';
  if (percentile <= 75) return 'bg-emerald-100 text-emerald-700';
  if (percentile <= 95) return 'bg-cyan-100 text-cyan-700';
  return 'bg-blue-100 text-blue-700';
};

export const getHandgripInterpretationText = (payload: HandgripResultPayload | null | undefined): string => {
  const summary = summarizeHandgripResult(payload);
  if (summary.bestOverall == null) {
    return 'Não há tentativas válidas registradas para interpretar o teste de Handgrip.';
  }

  const percentileLabel = summary.absolutePercentile == null
    ? 'sem percentil absoluto disponível'
    : summary.absolutePercentile < 5
      ? `abaixo do percentil 5 para força absoluta${summary.absoluteReferenceP5 != null ? ` (P5: ${summary.absoluteReferenceP5.toFixed(1)} ${summary.unit})` : ''}`
      : summary.absolutePercentile > 95
        ? 'acima do percentil 95 para força absoluta'
        : `no percentil absoluto ${formatHandgripPercentile(summary.absolutePercentile).replace('P', '')}`;
  const normalizedLabel =
    summary.normalizedOverall != null && summary.normalizedPercentile != null
      ? `valor normalizado ${summary.normalizedOverall.toFixed(2)} kg/m2 (${formatHandgripPercentile(summary.normalizedPercentile)}${summary.normalizedPercentile < 5 && summary.normalizedReferenceP5 != null ? `; P5: ${summary.normalizedReferenceP5.toFixed(1)} kg/m2` : ''})`
      : 'sem comparação normalizada';

  return resolveAssessmentInterpretationText(
    payload,
    `Melhor preensão palmar de ${summary.bestOverall.toFixed(1)} ${summary.unit} na mão ${summary.bestOverallSideLabel.toLowerCase()}: ${percentileLabel} e ${normalizedLabel}.`,
  );
};

interface HandgripResultRendererProps {
  payload: HandgripResultPayload | null | undefined;
  chartImages?: HandgripChartImages | null;
}

const SummaryMetric = ({ label, value, note }: { label: string; value: string; note?: string | null }) => (
  <div className="rounded-[24px] border border-slate-100 bg-white p-5 shadow-sm">
    <div className="text-[10px] font-black uppercase tracking-[0.22em] text-slate-400">{label}</div>
    <div className="mt-2 text-3xl font-black text-slate-900">{value}</div>
    {note ? <div className="mt-2 text-sm text-slate-500">{note}</div> : null}
  </div>
);

const AttemptsCard = ({ title, attempts }: { title: string; attempts: number[] }) => (
  <div className="rounded-[24px] border border-slate-100 bg-slate-50 p-5 shadow-sm">
    <div className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">{title}</div>
    <div className="mt-3 text-sm text-slate-700">
      {attempts.length ? attempts.map((value) => `${value.toFixed(1)} kg`).join(' · ') : 'Nenhuma tentativa registrada'}
    </div>
  </div>
);

export default function HandgripResultRenderer({ payload }: HandgripResultRendererProps) {
  const normalized = normalizeHandgripPayload(payload);
  const summary = summarizeHandgripResult(payload);

  if (!normalized) {
    return (
      <div className="rounded-[24px] border border-slate-100 bg-white p-6 shadow-sm">
        <div className="text-sm text-slate-500">Dados do Handgrip indisponíveis para este resultado.</div>
      </div>
    );
  }

  const severityLabel = getHandgripSeverityLabel(summary.absolutePercentile);
  const severityTone = getHandgripSeverityTone(summary.absolutePercentile);
  const scoreTone =
    summary.absolutePercentile == null
      ? 'text-primary-800'
      : summary.absolutePercentile < 5
        ? 'text-rose-600'
        : summary.absolutePercentile < 25
          ? 'text-amber-600'
          : summary.absolutePercentile <= 75
            ? 'text-emerald-600'
            : 'text-cyan-700';
  const interpretation = getHandgripInterpretationText(normalized);
  const absoluteChartData = getHandgripChartData(normalized.norms.sex, 'absolute');
  const normalizedChartData = getHandgripChartData(normalized.norms.sex, 'normalized');
  const absoluteAxis = getHandgripChartAxisConfig(absoluteChartData, summary.bestOverall, 'absolute');
  const normalizedAxis = getHandgripChartAxisConfig(normalizedChartData, summary.normalizedOverall, 'normalized');

  return (
    <div className="space-y-6">
      <div className="grid gap-6 md:grid-cols-5">
        <div className="rounded-[28px] border border-slate-200 bg-white p-8 text-center shadow-sm md:col-span-2">
          <p className="mb-4 text-xs font-black uppercase tracking-[0.25em] text-primary-700">Handgrip</p>
          <div className={`text-6xl font-black leading-none ${scoreTone}`}>
            {summary.bestOverall != null ? summary.bestOverall.toFixed(1) : '—'}
          </div>
          <div className="mt-2 text-sm font-semibold text-slate-700">
            {summary.bestOverall != null ? `kg • ${summary.bestOverallSideLabel}` : 'sem valor válido'}
          </div>
          <div className={`mt-4 inline-flex rounded-full px-4 py-1.5 text-xs font-bold shadow-sm ${severityTone}`}>{severityLabel}</div>
        </div>

        <div className="flex flex-col justify-center space-y-4 md:col-span-3">
          <div>
            <h4 className="text-2xl font-black tracking-tight text-slate-800">Resumo normativo</h4>
            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-slate-600">{interpretation}</p>
          </div>

          <div className="grid gap-3 text-sm text-slate-600 md:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
              Dominância registrada: <span className="font-semibold text-slate-900">{getHandgripDominantHandLabel(normalized.dominantHand)}</span>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
              Sexo: <span className="font-semibold text-slate-900">{normalized.norms.sex === 'F' ? 'Feminino' : 'Masculino'}</span> • Idade:{' '}
              <span className="font-semibold text-slate-900">{normalized.norms.ageYears != null ? `${normalized.norms.ageYears} anos` : '—'}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
        <SummaryMetric
          label="Melhor direita"
          value={summary.bestRight != null ? `${summary.bestRight.toFixed(1)} kg` : '—'}
          note={summary.meanRight != null ? `Média ${summary.meanRight.toFixed(1)} kg` : null}
        />
        <SummaryMetric
          label="Melhor esquerda"
          value={summary.bestLeft != null ? `${summary.bestLeft.toFixed(1)} kg` : '—'}
          note={summary.meanLeft != null ? `Média ${summary.meanLeft.toFixed(1)} kg` : null}
        />
        <SummaryMetric
          label="Percentil absoluto"
          value={formatHandgripPercentile(summary.absolutePercentile)}
          note={summary.absolutePercentPredicted != null ? `${summary.absolutePercentPredicted.toFixed(0)}% do P50` : null}
        />
        <SummaryMetric
          label="Normalizado"
          value={summary.normalizedOverall != null ? `${summary.normalizedOverall.toFixed(2)} kg/m2` : '—'}
          note={
            summary.normalizedPercentile != null
              ? `${formatHandgripPercentile(summary.normalizedPercentile)} • ${summary.normalizedPercentPredicted?.toFixed(0) ?? '—'}% do P50`
              : null
          }
        />
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <AttemptsCard title="Tentativas direita" attempts={summary.rightAttempts} />
        <AttemptsCard title="Tentativas esquerda" attempts={summary.leftAttempts} />
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <div className="rounded-[24px] border border-slate-100 bg-white p-6 shadow-sm">
          <div className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-400">Referências absolutas</div>
          <div className="mt-4 space-y-2 text-sm text-slate-700">
            <div className="flex items-center justify-between">
              <span>Referência mediana (P50)</span>
              <span className="font-semibold text-slate-900">
                {normalized.norms.absoluteKg.predictedP50 != null ? `${normalized.norms.absoluteKg.predictedP50.toFixed(1)} kg` : '—'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span>Limite inferior (P5)</span>
              <span className="font-semibold text-slate-900">
                {normalized.norms.absoluteKg.referenceP5 != null ? `${normalized.norms.absoluteKg.referenceP5.toFixed(1)} kg` : '—'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span>Posição percentílica</span>
              <span className="font-semibold text-slate-900">
                {formatHandgripPercentile(normalized.norms.absoluteKg.percentile)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span>Resultado em relação ao P50</span>
              <span className="font-semibold text-slate-900">
                {normalized.norms.absoluteKg.percentPredicted != null ? `${normalized.norms.absoluteKg.percentPredicted.toFixed(0)}%` : '—'}
              </span>
            </div>
          </div>
        </div>

        <div className="rounded-[24px] border border-slate-100 bg-white p-6 shadow-sm">
          <div className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-400">Referências normalizadas</div>
          <div className="mt-4 space-y-2 text-sm text-slate-700">
            <div className="flex items-center justify-between">
              <span>Referência mediana (P50)</span>
              <span className="font-semibold text-slate-900">
                {normalized.norms.normalizedKgPerM2.predictedP50 != null
                  ? `${normalized.norms.normalizedKgPerM2.predictedP50.toFixed(1)} kg/m2`
                  : '—'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span>Limite inferior (P5)</span>
              <span className="font-semibold text-slate-900">
                {normalized.norms.normalizedKgPerM2.referenceP5 != null
                  ? `${normalized.norms.normalizedKgPerM2.referenceP5.toFixed(1)} kg/m2`
                  : '—'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span>Posição percentílica</span>
              <span className="font-semibold text-slate-900">
                {formatHandgripPercentile(normalized.norms.normalizedKgPerM2.percentile)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span>Resultado em relação ao P50</span>
              <span className="font-semibold text-slate-900">
                {normalized.norms.normalizedKgPerM2.percentPredicted != null
                  ? `${normalized.norms.normalizedKgPerM2.percentPredicted.toFixed(0)}%`
                  : '—'}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-[24px] border border-slate-100 bg-white p-6 shadow-sm">
        <div className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-400">Curvas percentílicas - absoluto</div>
        <div className="mt-4">
          <div className="h-72 rounded-2xl border border-slate-100 bg-slate-50 p-2">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={absoluteChartData} margin={{ top: 8, right: 16, bottom: 28, left: 18 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  dataKey="age"
                  type="number"
                  domain={absoluteAxis.xDomain}
                  ticks={absoluteAxis.xTicks}
                  tickFormatter={(value) => (value === 100 ? '100+' : String(value))}
                  label={{ value: absoluteAxis.xLabel, position: 'insideBottom', offset: -16 }}
                  allowDataOverflow
                />
                <YAxis
                  domain={absoluteAxis.yDomain}
                  ticks={absoluteAxis.yTicks}
                  label={{ value: absoluteAxis.yLabel, angle: -90, position: 'insideLeft' }}
                  allowDataOverflow
                  width={64}
                />
                <Tooltip
                  labelFormatter={(label, items) => formatHandgripChartAgeBand(items?.[0]?.payload, label)}
                  formatter={(value: unknown, name: string | number) => [
                    typeof value === 'number' ? `${value.toFixed(1)} kg` : value,
                    String(name),
                  ]}
                  itemSorter={(item) => -Number(item.value ?? 0)}
                />
                {TOMKINSON_HANDGRIP_2025_PERCENTILES.map((percentile) => (
                  <Line
                    key={`renderer-abs-${percentile}`}
                    type="monotone"
                    dataKey={`P${percentile}`}
                    stroke={percentile === 50 ? '#2563eb' : '#94a3b8'}
                    strokeWidth={percentile === 50 ? 2 : 1}
                    dot={false}
                    isAnimationActive={false}
                  />
                ))}
                {summary.bestOverall != null && summary.plotAge != null ? (
                  <ReferenceDot x={summary.plotAge} y={summary.bestOverall} r={4} fill="#ef4444" stroke="#b91c1c" />
                ) : null}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="rounded-[24px] border border-slate-100 bg-white p-6 shadow-sm">
        <div className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-400">Curvas percentílicas - normalizado</div>
        <div className="mt-4">
          <div className="h-72 rounded-2xl border border-slate-100 bg-slate-50 p-2">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={normalizedChartData} margin={{ top: 8, right: 16, bottom: 28, left: 18 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  dataKey="age"
                  type="number"
                  domain={normalizedAxis.xDomain}
                  ticks={normalizedAxis.xTicks}
                  tickFormatter={(value) => (value === 100 ? '100+' : String(value))}
                  label={{ value: normalizedAxis.xLabel, position: 'insideBottom', offset: -16 }}
                  allowDataOverflow
                />
                <YAxis
                  domain={normalizedAxis.yDomain}
                  ticks={normalizedAxis.yTicks}
                  label={{ value: normalizedAxis.yLabel, angle: -90, position: 'insideLeft' }}
                  allowDataOverflow
                  width={64}
                />
                <Tooltip
                  labelFormatter={(label, items) => formatHandgripChartAgeBand(items?.[0]?.payload, label)}
                  formatter={(value: unknown, name: string | number) => [
                    typeof value === 'number' ? `${value.toFixed(1)} kg/m²` : value,
                    String(name),
                  ]}
                  itemSorter={(item) => -Number(item.value ?? 0)}
                />
                {TOMKINSON_HANDGRIP_2025_PERCENTILES.map((percentile) => (
                  <Line
                    key={`renderer-norm-${percentile}`}
                    type="monotone"
                    dataKey={`P${percentile}`}
                    stroke={percentile === 50 ? '#2563eb' : '#94a3b8'}
                    strokeWidth={percentile === 50 ? 2 : 1}
                    dot={false}
                    isAnimationActive={false}
                  />
                ))}
                {summary.normalizedOverall != null && summary.plotAge != null ? (
                  <ReferenceDot x={summary.plotAge} y={summary.normalizedOverall} r={4} fill="#ef4444" stroke="#b91c1c" />
                ) : null}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="rounded-[24px] border border-slate-100 bg-slate-50 p-5 text-xs text-slate-500 shadow-sm">{summary.citation}</div>

      {summary.notes ? (
        <div className="rounded-[24px] border border-slate-100 bg-white p-5 shadow-sm">
          <div className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Observações</div>
          <div className="mt-3 whitespace-pre-wrap text-sm text-slate-700">{summary.notes}</div>
        </div>
      ) : null}
    </div>
  );
}
